import { FC, useEffect, useState } from 'react';
import Modal from '@components/Modal';
import { Document } from '@components/DocumentForm';
import { nanoid } from 'nanoid';
import Button from '@components/DesignLibrary/Button';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { DatePicker, Select, Input, Checkbox, NumberInput, TextArea } from '@/components/DesignLibrary/Atoms';
import * as format from '@format';
import { IncomeMetadata } from '@documentsApi/document-api-models';

import s from './AddIncomeSourceModal.module.scss';

type Props = {
  onClose: () => void;
  documentRequestUserId: string;
  refetchFiles: () => Promise<void>;
  refetchConsolidatedData: () => Promise<void>;
  isTableViewOpen: boolean;
};

export const incomeSourceOptions=[
  {
    key: 'Employment',
    label: 'Employment',
  },
  {
    key: 'Social Security',
    label: 'Social Security',
  },
  {
    key: 'Rental Property income',
    label: 'Rental Property income',
  },
  {
    key: 'Interest & Dividends',
    label: 'Interest & Dividends',
  },
  {
    key: 'Disability',
    label: 'Disability',
  },
  {
    key: 'Unemployment',
    label: 'Unemployment',
  },
  {
    key: 'Retirement/Pension',
    label: 'Retirement/Pension',
  },
  {
    key: 'Government assistance (welfare, food stamps, etc.)',
    label: 'Government assistance (welfare, food stamps, etc.)',
  },
  {
    key: 'Child support or alimony received',
    label: 'Child support or alimony received',
  },
  {
    key: 'Other',
    label: 'Other',
  },
];

const AddIncomeSourceModal: FC<Props> = ({ onClose, documentRequestUserId, refetchFiles, isTableViewOpen, refetchConsolidatedData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [document, setDocument] = useState<Document>({
    description: '',
    id: nanoid(),
    integrationFileName: null,
    integrationSlug: null,
    internalDataPromptHelp: null,
    internalDataType: null,
    isCollapsed: false,
    isForYourEyesOnly: false,
    isRequired: true,
    metadata: null,
    order: 1,
    title: '',
  });

  const [incomeSource, setIncomeSource] = useState<IncomeMetadata['incomeSource']>('Employment');
  const [monthlyDollarValue, setMonthlyDollarValue] = useState<number| null>(null);
  const [description, setDescription] = useState<string| null>(null);
  const [frequency, setFrequency] = useState<IncomeMetadata['payFrequency'] | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isEndDateRequired, setIsEndDateRequired] = useState(true);
  const [isCtaDisabled, setIsCtaDisabled] = useState(true);

  const { getData: addFileFn } = useNoodleApi(tsClient.documentRequests.createDocumentRequestUserFile);

  const resetFields =(): void => {
    setMonthlyDollarValue(null);
    setDescription(null);
    setFrequency(null);
    setStartDate(null);
    setEndDate(null);
    setIsEndDateRequired(true);
  };

  useEffect(() => {
    if (incomeSource==='Employment' && startDate && frequency) {
      setDocument(prev => ({
        ...prev,
        description: `${frequency ? `Pay Frequency: ${frequency}; ` : ''}${
          startDate ? `Start Date: ${format.datetimeLocalTZ.dateString(startDate)}` : ''
        }${(() => {
          if (isEndDateRequired) {
            if (endDate) {
              return `, End Date: ${format.datetimeLocalTZ.dateString(endDate)}`;
            }
            return '';
          }
          return ', still work there';
        })()}`,
        metadata: {
          endDate: endDate && isEndDateRequired ? format.datetimeLocalTZ.dateString(endDate) : null,
          incomeSource,
          monthlyDollarValue:null,
          payFrequency: frequency,
          startDate: format.datetimeLocalTZ.dateString(startDate),
        },
      }));
    }else if(incomeSource!=='Employment') {
      setDocument(prev => ({
        ...prev,
        description: (description && monthlyDollarValue) ? `${description} - Monthly amount: $ ${monthlyDollarValue}`: description || (monthlyDollarValue? `Monthly amount: $ ${monthlyDollarValue}` : ''),
        metadata: {
          endDate: null,
          incomeSource,
          monthlyDollarValue,
          payFrequency: 'Monthly',
          startDate: null,
        },
      }));
    }
  }, [startDate, endDate, isEndDateRequired, frequency, incomeSource, description, monthlyDollarValue]);

  useEffect(() => {
    const emptyDoc = {
      description: '',
      id: nanoid(),
      integrationFileName: null,
      integrationSlug: null,
      internalDataPromptHelp: null,
      internalDataType: null,
      isCollapsed: false,
      isForYourEyesOnly: false,
      isRequired: true,
      metadata: null,
      order: 1,
      title: '',
    };
    if(incomeSource==='Employment') {
      setDocument(emptyDoc);
    }
    else setDocument({...emptyDoc, isRequired: false, title: incomeSource});

    resetFields();
  },[incomeSource]);

  // controls enabling/disabling of CTA
  useEffect(() => {
    if(incomeSource==='Employment') setIsCtaDisabled(!document.title || !frequency);
    else if(incomeSource!=='Other') setIsCtaDisabled(!monthlyDollarValue);
    else setIsCtaDisabled(!monthlyDollarValue && !description);
  },[incomeSource,document.title,frequency,monthlyDollarValue,description]);

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    await addFileFn({
      ...document,
      id: documentRequestUserId,
    });
    await refetchFiles();
    if ( isTableViewOpen ) {
      await refetchConsolidatedData();
    }
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Modal hideBottomInset title="Add Income Source" onClose={onClose}>
      <div className={s.container}>
        <Select
          label="Income Source"
          value={incomeSource}
          onChange={val => setIncomeSource(val as IncomeMetadata['incomeSource'])}
          options={incomeSourceOptions}
        />
        {incomeSource==='Employment' && <>
          <Input
            value={document.title}
            onChange={val =>
              setDocument(prev => ({
                ...prev,
                title: val,
              }))
            }
            label="Employer name"
          />
          <Select
            label="Pay frequency"
            value={frequency}
            onChange={val => setFrequency(val as IncomeMetadata['payFrequency'])}
            options={[
              {
                key: 'Weekly',
                label: 'Weekly',
              },
              {
                key: 'Biweekly',
                label: 'Biweekly',
              },
              {
                key: 'Monthly',
                label: 'Monthly',
              },
              {
                key: 'Quarterly',
                label: 'Quarterly',
              },
              {
                key: 'Annually',
                label: 'Annually',
              },
            ]}
          />
          <DatePicker label="Start date" value={startDate} onChange={val => setStartDate(val)} />
          <Checkbox label="Still work there" isChecked={!isEndDateRequired} onChange={e => setIsEndDateRequired(!e)} />
          {isEndDateRequired && <DatePicker label="End date" value={endDate} onChange={val => setEndDate(val)} />}
        </>}

        {
          incomeSource==='Other'
          && <TextArea
            value={description}
            onChange={val =>
              setDescription(val)
            }
            label="Description"
          />
        }

        {incomeSource!=='Employment' && <NumberInput
          value={monthlyDollarValue}
          onChange={val =>
            setMonthlyDollarValue(val)
          }
          label="Monthly dollar value"
          min={0}
        />}

        <Button loading={isSubmitting} disabled={isCtaDisabled} variant="primary" size="md" fullWidth onClick={handleSubmit}>
          Add Income Source
        </Button>
      </div>
    </Modal>
  );
};

export default AddIncomeSourceModal;
